import { z } from 'zod';

/**
 * Shared limits for the feedback widget — single source of truth so the
 * frontend Sheet, the backend route validator, and the Zod schema below
 * all stay in lockstep when limits change.
 */
export const FEEDBACK_LIMITS = {
  maxAttachments: 5,
  maxAttachmentBytes: 10 * 1024 * 1024, // 10 MB per file
  maxTotalBytes: 25 * 1024 * 1024, // 25 MB combined
  maxSubjectLength: 200,
  maxMessageLength: 10_000,
} as const;

/**
 * Backend allowlist of acceptable attachment content types. The frontend
 * `accept=` attribute is a UI hint only — this list is the security
 * boundary enforced server-side. Add to it deliberately; we don't want
 * arbitrary executables flowing into support's inbox.
 */
export const FEEDBACK_ALLOWED_CONTENT_TYPES: readonly string[] = [
  // Images
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  // Documents
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/json',
  'text/markdown',
  // Spreadsheets
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // Archives (logs / bundles)
  'application/zip',
  'application/x-zip-compressed',
  // Logs frequently arrive as text/plain or octet-stream from the file picker
  'application/octet-stream',
];

export const feedbackContextSchema = z.object({
  url: z.string().min(1),
  userAgent: z.string().min(1),
  viewport: z.string().optional(),
  timestamp: z.string().min(1),
});

export const submitFeedbackSchema = z.object({
  subject: z.string().min(1).max(FEEDBACK_LIMITS.maxSubjectLength),
  message: z.string().min(1).max(FEEDBACK_LIMITS.maxMessageLength),
  context: feedbackContextSchema,
});

export type FeedbackContext = z.infer<typeof feedbackContextSchema>;
export type SubmitFeedbackRequest = z.infer<typeof submitFeedbackSchema>;

export interface SubmitFeedbackResponse {
  status: 'sent';
}
